import React, { useEffect, useState } from 'react';
import { Plus, CheckSquare, Search, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { useModal } from '../components/AppModal';
import useUserPreference from '../hooks/useUserPreference';
import useViewMode from '../hooks/useViewMode';
import useFieldLayout from '../hooks/useFieldLayout';
import VistaControles from '../components/VistaControles';
import DataTable from '../components/DataTable';

const initialForm = {
  subject: '',
  description: '',
  status: 'pendiente',
  priority: 'media',
  dueDate: ''
};

const Activities = () => {
  const { hasPermission } = useAuth();
  const [activities, setActivities] = useState([]);
  const [search, setSearch] = useUserPreference('activities:search', '');
  const [viewMode, setViewMode] = useViewMode('activities');
  const fl = useFieldLayout('activities', viewMode);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(true);
  const modal = useModal();

  useEffect(() => {
    loadActivities();
  }, []);

  const loadActivities = async () => {
    try {
      const res = await api.get('/activities');
      setActivities(res.data);
    } catch (error) {
      modal.error('Error cargando actividades');
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setForm(initialForm);
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    try {
      const res = await api.post('/activities', form);
      setActivities([res.data, ...activities]);
      closeModal();
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error creando actividad');
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      const res = await api.put(`/activities/${id}`, { status });
      setActivities((prev) => prev.map((item) => (item.id === id ? res.data : item)));
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error actualizando actividad');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/activities/${id}`);
      setActivities((prev) => prev.filter((item) => item.id !== id));
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error eliminando actividad');
    }
  };

  const filtered = activities.filter((activity) =>
    [activity.subject, activity.description]
      .filter(Boolean)
      .some((value) => value.toLowerCase().includes(search.toLowerCase()))
  );

  const getPriorityLabel = (priority) => ({
    baja: 'Baja',
    media: 'Media',
    alta: 'Alta'
  }[priority] || priority);

  const getPriorityBadgeClass = (priority) => ({
    baja: 'badge-info',
    media: 'badge-warning',
    alta: 'badge-danger'
  }[priority] || 'badge-gray');

  const getStatusLabel = (status) => ({
    pendiente: 'Pendiente',
    en_progreso: 'En progreso',
    completada: 'Completada',
    cancelada: 'Cancelada'
  }[status] || status);

  const getStatusBadgeClass = (status) => ({
    pendiente: 'badge-warning',
    en_progreso: 'badge-info',
    completada: 'badge-success',
    cancelada: 'badge-gray'
  }[status] || 'badge-gray');

  const columns = [
    { key: 'subject', header: 'Asunto', className: 'dt-strong', fixed: true, render: (a) => (
      <div>
        {a.subject}
        {a.description && <div className="dt-sub" style={{ fontWeight: 400 }}>{a.description}</div>}
      </div>
    ) },
    { key: 'priority', header: 'Prioridad', render: (a) => (
      <span className={`badge ${getPriorityBadgeClass(a.priority)}`}>{getPriorityLabel(a.priority)}</span>
    ) },
    { key: 'status', header: 'Estado', render: (a) => (
      <span className={`badge ${getStatusBadgeClass(a.status)}`}>{getStatusLabel(a.status)}</span>
    ) },
    { key: 'dueDate', header: 'Vencimiento', className: 'dt-nowrap', render: (a) => (
      a.dueDate ? new Date(a.dueDate).toLocaleDateString('es-ES') : 'Sin fecha'
    ) },
    { key: 'actions', header: '', className: 'col-actions', render: (a) => (
      <div className="dt-actions">
        <select
          className="form-input"
          value={a.status}
          onChange={(event) => handleStatusChange(a.id, event.target.value)}
        >
          <option value="pendiente">Pendiente</option>
          <option value="en_progreso">En progreso</option>
          <option value="completada">Completada</option>
          <option value="cancelada">Cancelada</option>
        </select>
        {hasPermission('activities', 'delete') && (
          <button className="btn-icon" onClick={() => handleDelete(a.id)} style={{ color: '#ef4444' }}>
            <Trash2 size={16} />
          </button>
        )}
      </div>
    ) },
  ];

  const fields = fl.fields(columns);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Actividades</h1>
          <p className="page-subtitle">{activities.length} actividades registradas</p>
        </div>
        {hasPermission('activities', 'create') && (
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            <Plus size={18} /> Nueva actividad
          </button>
        )}
      </div>

      <div className="card">
        <div className="search-bar">
          <div className="search-input-wrapper">
            <Search />
            <input
              type="text"
              className="form-input"
              placeholder="Buscar actividad..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <VistaControles
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              fields={fields}
              hidden={fl.hidden}
              onToggleField={fl.toggle}
              onReorderFields={fl.reorder}
            />
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Cargando...</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <CheckSquare size={64} />
            <h3>No hay actividades</h3>
            <p>Crea tu primera actividad para comenzar</p>
          </div>
        ) : viewMode === 'table' ? (
          <DataTable columns={fl.orderColumns(columns)} rows={filtered} hiddenKeys={fl.hidden} />
        ) : (
          <div className="items-grid">
            {filtered.map((activity) => (
              <div className="item-card" key={activity.id}>
                <div className="item-card-header">
                  <div className="item-card-main">
                    <div className="item-card-avatar">
                      <CheckSquare size={18} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="item-card-title">{activity.subject}</div>
                      {activity.description && (
                        <div className="item-card-note" style={{ marginTop: 6 }}>
                          {activity.description}
                        </div>
                      )}
                    </div>
                  </div>
                  {!fl.isHidden('priority') && (
                    <span className={`badge ${getPriorityBadgeClass(activity.priority)}`}>
                      {getPriorityLabel(activity.priority)}
                    </span>
                  )}
                </div>

                <div className="item-card-body">
                  {!fl.isHidden('status') && (
                    <div className="item-card-row" style={fl.cardStyle('status')}>
                      <span className="item-label">Estado actual</span>
                      <span className={`badge ${getStatusBadgeClass(activity.status)}`}>
                        {getStatusLabel(activity.status)}
                      </span>
                    </div>
                  )}
                  {!fl.isHidden('dueDate') && (
                    <div className="item-card-row" style={fl.cardStyle('dueDate')}>
                      <span className="item-label">Vencimiento</span>
                      <span>{activity.dueDate ? new Date(activity.dueDate).toLocaleDateString('es-ES') : 'Sin fecha'}</span>
                    </div>
                  )}
                </div>

                <div className="item-card-footer">
                  <div style={{ flex: 1, minWidth: 180 }}>
                    <label className="item-label" style={{ display: 'block', marginBottom: 6 }}>
                      Cambiar estado
                    </label>
                    <select
                      className="form-input"
                      value={activity.status}
                      onChange={(event) => handleStatusChange(activity.id, event.target.value)}
                    >
                      <option value="pendiente">Pendiente</option>
                      <option value="en_progreso">En progreso</option>
                      <option value="completada">Completada</option>
                      <option value="cancelada">Cancelada</option>
                    </select>
                  </div>

                  {hasPermission('activities', 'delete') && (
                    <div className="item-card-actions">
                      <button className="btn-icon" onClick={() => handleDelete(activity.id)} style={{ color: '#ef4444' }}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <h2>Nueva actividad</h2>
              <button className="btn-icon" onClick={closeModal}>&times;</button>
            </div>
            <form onSubmit={handleCreate}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Asunto *</label>
                  <input
                    className="form-input"
                    value={form.subject}
                    onChange={(event) => setForm({ ...form, subject: event.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Descripción</label>
                  <textarea
                    className="form-input"
                    rows={3}
                    value={form.description}
                    onChange={(event) => setForm({ ...form, description: event.target.value })}
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Prioridad</label>
                    <select className="form-input" value={form.priority} onChange={(event) => setForm({ ...form, priority: event.target.value })}>
                      <option value="baja">Baja</option>
                      <option value="media">Media</option>
                      <option value="alta">Alta</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Vencimiento</label>
                    <input
                      type="date"
                      className="form-input"
                      value={form.dueDate}
                      onChange={(event) => setForm({ ...form, dueDate: event.target.value })}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={closeModal}>Cancelar</button>
                <button type="submit" className="btn btn-primary">Crear actividad</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Activities;
